import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { VolumeOff, VolumeUp } from '@mui/icons-material';
import CountdownTimer from './CountdownTimer';
import videoSrc from './assets/video.mp4';
import Background from '../layout/Background';

export default function Home() {
    const [mounted, setMounted] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const videoRef = useRef(null);

    // Fecha del evento: 6 de febrero de 2026, 00:00
    const targetDate = '2026-02-06T00:00:00';

    useEffect(() => {
        setMounted(true);
    }, []);

    return (
        <Box sx={{
            position: 'relative',
            width: '100%',
            height: window.innerHeight,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            paddingTop: '80px',
            boxSizing: 'border-box',
            backgroundColor: '#000000',
        }}>
            {/* Video de fondo - todo el ancho */}
            <video
                ref={videoRef}
                autoPlay
                loop
                muted={isMuted}
                playsInline
                preload="auto"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    zIndex: 0,
                    backgroundColor: '#000000',
                }}
                onError={(e) => {
                    console.error('Error cargando video:', e);
                }}
            >
                <source src={videoSrc} type="video/mp4" />
            </video>

            {/* Botón de volumen - abajo a la izquierda */}
            <IconButton
                onClick={() => {
                    setIsMuted(!isMuted);
                    if (videoRef.current) {
                        videoRef.current.muted = !isMuted;
                    }
                }}
                sx={{
                    position: 'absolute',
                    bottom: { xs: 16, sm: 24, md: 32 },
                    left: { xs: 16, sm: 24, md: 32 },
                    zIndex: 3,
                    backgroundColor: "#220015",
                    height: 50,
                    width: 50,
                    borderRadius: '10px',
                    '&:hover': {
                        backgroundColor: "#220015",
                    },
                }}
                aria-label={isMuted ? 'Activar sonido' : 'Desactivar sonido'}
            >
                {isMuted ? <VolumeOff sx={{ color: "#ccc", fontSize: 25 }} /> : <VolumeUp sx={{ color: "#ccc", fontSize: 25 }} />}
            </IconButton>

            {/* Overlay degradado horizontal para mejor legibilidad */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.0) 100%)',
                    zIndex: 1,
                }}
            />

            {/* Contenido superpuesto */}
            <Box sx={{
                position: 'relative',
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', md: 'flex-start' },
                width: '100%',
                px: { xs: 2, sm: 4, md: 6 },
                pt: { xs: 4, sm: 5, md: 6 },
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 1s ease-out, transform 1s ease-out',
            }}>
                {/* Título principal y contador - Izquierda */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 0,
                    animation: 'fadeInUp 1s ease-out',
                    alignItems: 'flex-start',
                    textAlign: 'left',
                    flex: { md: '1 1 auto' },
                    maxWidth: { md: '60%' },
                    mt: { xs: 4, sm: 5, md: 8 },
                    px: { xs: 2, sm: 3, md: 4 },
                }}>
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'inline-block',
                        }}
                    >
                        <Typography
                            component="div"
                            sx={{
                                fontSize: { xs: '2.5rem', sm: '3.2rem', md: '3.5rem', lg: '4rem' },
                                fontWeight: 600,
                                color: '#FFFFFF',
                                lineHeight: 1.1,
                                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.25)',
                                letterSpacing: 'normal',
                                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                                textAlign: 'left',
                            }}
                        >
                            <Background backgroundColor="transparent" countStars={40} />
                            La ciudad ya se prepara para{' '}
                            <Box
                                component="span"
                                sx={{
                                    color: '#D9A441',
                                }}
                            >
                                vivirlo con vos
                            </Box>
                        </Typography>
                    </Box>
                    
                    {/* Countdown Timer - Debajo del título */}
                    <Box sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        mt: { xs: 3, sm: 3.5, md: 4 },
                        width: '100%',
                    }}>
                        <CountdownTimer targetDate={targetDate} />
                    </Box>
                </Box>
            </Box>

            {/* Estilos de animación */}
            <style>
                {`
                    @keyframes float {
                        0%, 100% {
                            transform: translateY(0) translateX(0);
                            opacity: 0.3;
                        }
                        25% {
                            transform: translateY(-20px) translateX(10px);
                            opacity: 0.6;
                        }
                        50% {
                            transform: translateY(-40px) translateX(-10px);
                            opacity: 0.4;
                        }
                        75% {
                            transform: translateY(-20px) translateX(5px);
                            opacity: 0.5;
                        }
                    }
                    
                    @keyframes fadeInUp {
                        from {
                            opacity: 0;
                            transform: translateY(30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                `}
            </style>
        </Box>
    );
};
